import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverDoughnutsArticle() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-doughnuts-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Top Doughnut Spots",
    "section-2": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/food-drink/desserts/",
      label: "Destination Vancouver – Desserts & Sweet Treats",
    },
    {
      url: "https://tripplanning.translink.ca/",
      label: "TransLink Trip Planner",
    },
    {
      text: "Georgia Straight (2024). 'The 10 best doughnut places in Metro Vancouver.'",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have 10 spots from the conversation. Each location has a unique ID to match <Marker>.
  // Precisely match lat/lng from the conversation (to 5 decimals).
  // We'll group them all under "section-1" to keep the structure simpler, but we’ll reference them individually in the text.
  const locations = [
    {
      id: "lucky",
      lat: 49.25800,
      lng: -123.10125,
      popupText: "Lucky’s Doughnuts (Main Street)",
    },
    {
      id: "lees",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Lee’s Donuts (Granville Island)",
    },
    {
      id: "honeys",
      lat: 49.32691,
      lng: -122.94868,
      popupText: "Honey Doughnuts & Goodies (Deep Cove)",
    },
    {
      id: "harmony",
      lat: 49.34074,
      lng: -123.07283,
      popupText: "Harmony Donut Shop (North Vancouver)",
    },
    {
      id: "duffins",
      lat: 49.23236,
      lng: -123.08056,
      popupText: "Duffin’s Donuts (24/7 donut & deli)",
    },
    {
      id: "cartems",
      lat: 49.26343,
      lng: -123.10005,
      popupText: "Cartems Donuts (Mount Pleasant; now closed)",
    },
    {
      id: "mello",
      lat: 49.28042,
      lng: -123.10000,
      popupText: "Mello (Chinatown)",
    },
    {
      id: "punkrock",
      lat: 49.28105,
      lng: -122.99870,
      popupText: "Punk Rock Pastries (Burnaby)",
    },
    {
      id: "doughnutlove",
      lat: 49.26604,
      lng: -122.85029,
      popupText: "Doughnut Love (Coquitlam)",
    },
    {
      id: "oohlala",
      lat: 49.26808,
      lng: -123.15110,
      popupText: "Ooh La La Donuts (Kitsilano)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Sweetest Doughnuts: A Tasty Guide</title>
        <meta
          name="description"
          content="Explore Vancouver’s best doughnut spots, from classic favorites to gourmet vegan treats. Get tips, locations, and a sweet slice of local food culture."
        />
        <meta
          name="keywords"
          content="Vancouver doughnuts, best donuts, doughnut shops, local food, dessert guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-doughnuts-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Sweetest Doughnuts: A Tasty Guide"
        />
        <meta
          property="og:description"
          content="A comprehensive guide to Vancouver’s doughnut scene, featuring top spots, local regs, FAQ, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-doughnuts-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-doughnuts-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Sweetest Doughnuts: A Tasty Guide"
        />
        <meta
          name="twitter:description"
          content="Learn where to find Vancouver’s best doughnuts, complete with insider tips and local insights."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-doughnuts-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Vancouver Doughnut Tour"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Craving a sweet treat in Vancouver? You’re in the right place.
              Vancouver’s doughnut scene has exploded with creative flavors,
              classic recipes, and even vegan-friendly delights. Whether you’re
              a longtime local or a curious traveler, this guide will show you
              exactly where to find the fluffiest, most delectable doughnuts in
              the city—plus the basics on local guidelines, FAQs, and everything
              else you need to enjoy your sugar fix responsibly.
            </p>
            <p>
              In the sections below, we’ll spotlight Vancouver’s top 10 doughnut
              shops, each offering their own twist—from old-school classics to
              artisanal brioche confections. We’ll provide useful insider tips
              on peak hours, prices, dietary-friendly options, and more. Ready
              to dig in? Let’s go!
            </p>

            {/* SECTION: Section-1 (Top Doughnut Spots) */}
            <h2 id="section-1" ref={(el) => setSpotRef("section-1", el)}>
              Top Doughnut Spots
            </h2>
            <p>
              Vancouver is home to a diverse range of doughnut shops—each with a
              unique story, flavor specialty, and loyal following. Below are
              some must-visit spots along with their exact coordinates so you
              can easily map out your own doughnut adventure.
            </p>

            {/* 1) Lucky's Doughnuts */}
            <h3>Lucky’s Doughnuts (49th Parallel Café)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.25800}
                coordX={-123.10125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.25800, -123.10125)
            </p>
            <p>
              Lucky’s is known for artisanal doughnuts in inventive flavors like
              Peanut Butter and Jelly, Salted Caramel, and London Fog. With
              vegan and gluten-friendly options, it’s beloved by locals who
              enjoy premium coffee pairings from 49th Parallel. Go early for the
              best selection.
            </p>

            {/* 2) Lee's Donuts */}
            <h3>Lee’s Donuts (Granville Island)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.27262}
                coordX={-123.13524}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.27262, -123.13524)
            </p>
            <p>
              A classic since 1979, Lee’s Donuts inside Granville Island Public
              Market serves up timeless favorites like the Honey Dip and
              Chocolate Glazed. Affordably priced and freshly made, these
              doughnuts attract a line but are well worth the wait. Don’t miss
              grabbing a doughnut to enjoy by the water.
            </p>

            {/* 3) Honey Doughnuts & Goodies */}
            <h3>Honey Doughnuts & Goodies (Deep Cove)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.32691}
                coordX={-122.94868}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.32691, -122.94868)
            </p>
            <p>
              Tucked away in the beautiful Deep Cove area, Honey’s signature
              honey-glazed cake doughnuts are legendary—celebrities and hikers
              alike rave about their dense, comforting texture. Best enjoyed
              with a latte before or after exploring the local trails.
            </p>

            {/* 4) Harmony Donut Shop */}
            <h3>Harmony Donut Shop (North Vancouver)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.34074}
                coordX={-123.07283}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.34074, -123.07283)
            </p>
            <p>
              Stepping into Harmony is like stepping back in time. Family-run
              for over 50 years, they specialize in old-school cake doughnuts,
              often with simple icings or cinnamon sugar. A hidden gem that
              draws nostalgic locals seeking a “taste of the past.”
            </p>

            {/* 5) Duffin’s Donuts */}
            <h3>Duffin’s Donuts (24/7 Donut & Deli)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.23236}
                coordX={-123.08056}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.23236, -123.08056)
            </p>
            <p>
              If you need a doughnut fix at 3 AM, Duffin’s is the place. Open
              24/7, it offers a unique blend of doughnuts, fried chicken,
              sandwiches, and more. While the vibe is no-frills, the prices are
              low, and the doughnuts are consistently fresh.
            </p>

            {/* 6) Cartems Donuts (Closed) */}
            <h3>Cartems Donuts (Mount Pleasant, now closed)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.26343}
                coordX={-123.10005}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.26343, -123.10005)
            </p>
            <p>
              Though Cartems shuttered its doors in February 2025, it’s worth a
              mention as a pioneer of inventive flavors and vegan/GF doughnuts.
              Many Vancouver shops carry on the spirit with Earl Grey glazes and
              gourmet fillings Cartems helped popularize.
            </p>

            {/* 7) Mello */}
            <h3>Mello (Chinatown)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.28042}
                coordX={-123.10000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.28042, -123.10000)
            </p>
            <p>
              Mello’s brioche doughnuts are known for their fluffy texture and
              creative dessert-inspired fillings like Tiramisu or Cereal Milk.
              Lines can get long, so visit early. Seasonal offerings—like Lemon
              Curd or Strawberry Rhubarb—keep fans coming back for more.
            </p>

            {/* 8) Punk Rock Pastries */}
            <h3>Punk Rock Pastries (Burnaby)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.28105}
                coordX={-122.99870}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.28105, -122.99870)
            </p>
            <p>
              True to its name, this edgy bakery dishes out donuts with a rebel
              flair—think candied bacon toppings, spicy glazes, and fun
              punk-themed designs. Known for seasonal Halloween treats and
              custom orders. A must-try if you’re seeking a break from the
              ordinary.
            </p>

            {/* 9) Doughnut Love */}
            <h3>Doughnut Love (Coquitlam)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.26604}
                coordX={-122.85029}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.26604, -122.85029)
            </p>
            <p>
              Serving both yeast-raised and cake doughnuts, Doughnut Love
              focuses on small-batch, hand-cut treats. Their gluten-friendly
              options actually taste delicious (no small feat!), and they
              regularly rotate seasonal flavors, making each visit a sweet
              surprise.
            </p>

            {/* 10) Ooh La La Donuts */}
            <h3>Ooh La La Donuts (Kitsilano)</h3>
            <p>
              <strong>Coordinates: </strong>
              <Marker
                coordY={49.26808}
                coordX={-123.15110}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (49.26808, -123.15110)
            </p>
            <p>
              A family-owned French-inspired shop that overcame early setbacks
              to become a local favorite. Their crème brûlée donut torched on
              top is a standout. Expect a cozy interior and seasonal specials
              with a patisserie twist.
            </p>

            {/* SECTION: Section-2 (Local Regulations) */}
            <h2 id="section-2" ref={(el) => setSpotRef("section-2", el)}>
              Local Regulations
            </h2>
            <p>
              Doughnut shops in Vancouver follow food safety regulations under
              British Columbia law, ensuring proper handling and cleanliness.
              Expect staff to use gloves or tongs; if you have severe allergies,
              do ask about potential cross-contamination. While Vancouver’s
              doughnut scene is largely a casual experience, you’ll still want
              to follow typical city rules:
            </p>
            <ul>
              <li>
                <strong>Line Etiquette:</strong> Popular shops like Lee’s often
                have queues. Keep sidewalks clear and respect any posted signs.
              </li>
              <li>
                <strong>Payment Methods:</strong> Some older shops (Harmony,
                Honey’s) may only accept cash or debit. Bring small bills just
                in case.
              </li>
              <li>
                <strong>Parking:</strong> Watch for residential permits,
                especially in neighborhoods like Kitsilano and Deep Cove.
              </li>
              <li>
                <strong>Waste Disposal:</strong> Use the recycling and compost
                bins where available; Vancouver is big on sustainability.
              </li>
            </ul>
            <p>
              For any special events or food truck pop-ups, local vendors must
              have the necessary city permits. If you’re planning a doughnut
              crawl by bike, remember to wear a helmet (it’s the law in BC).
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. Which shops offer vegan doughnuts?</h3>
              <p>
                Lucky’s Doughnuts and Punk Rock Pastries often have rotating
                vegan options. Cartems was famous for vegan/GF before closing,
                but Mello occasionally features vegan specials. Always call or
                check their social media to confirm daily offerings.
              </p>

              <h3>2. Are there any 24-hour doughnut spots in Vancouver?</h3>
              <p>
                Yes! Duffin’s Donuts is open around the clock, offering fresh
                doughnuts alongside fried chicken and sandwiches. It’s the go-to
                for late-night or early-morning cravings.
              </p>

              <h3>3. Where should I park if I visit multiple doughnut shops?</h3>
              <p>
                Granville Island (Lee’s) offers paid parking but can be busy.
                Deep Cove (Honey’s) has limited street parking—arrive early.
                Downtown spots near Lucky’s or Mello may require metered
                parking. Consider public transit (see TransLink) to avoid
                parking headaches altogether.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              From classic Canadian doughnuts at Lee’s to the edgy creations at
              Punk Rock Pastries, Vancouver’s doughnut landscape is sweet,
              diverse, and constantly evolving. Whether you’re a first-time
              visitor or a seasoned doughnut devotee, exploring these spots is a
              delicious way to experience the city’s unique blend of tradition
              and innovation. Don’t forget to share the doughnut love—bring some
              home for friends, and keep supporting these local businesses that
              bring a taste of joy to Vancouver’s streets.
            </p>
            <p>
              <strong>Disclaimer:</strong> Hours, menus, and availability can
              change. Always check official sources or contact the shop for the
              latest details, especially regarding dietary restrictions or large
              group orders.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}